import {
  ChangeDetectionStrategy, Component, computed, effect, forwardRef, input, model, signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { HlmIconComponent } from './icon.component';
import { HlmInputDirective } from './input.directive';
import { HlmBtnDirective } from './button.directive';
import type { HlmSelectOption } from './select.component';

/**
 * Searchable combobox: an `hlmInput` text field with a Material autocomplete
 * dropdown (type-to-filter, keyboard nav, click-outside-close) and a trailing
 * clear/chevron affordance. Same option API + ControlValueAccessor as
 * `hlm-select`, so it is a drop-in for a searchable select — but rendered as a
 * text input to match the Add-Billing "Category/Feature" pickers (EB-4006).
 */
@Component({
  selector: 'hlm-combobox',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatAutocompleteModule, HlmIconComponent, HlmInputDirective, HlmBtnDirective],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HlmComboboxComponent), multi: true }],
  template: `
    <div class="relative w-full">
      <input
        hlmInput
        class="w-full pr-9"
        [attr.id]="inputId()"
        [formControl]="searchCtrl"
        [matAutocomplete]="auto"
        [placeholder]="placeholder()"
        (input)="onInput()"
        (focus)="selectAll($event)"
        (blur)="onTouched()"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        [displayWith]="displayFn"
        (optionSelected)="onSelected($event)"
        (opened)="panelOpen.set(true)"
        (closed)="panelOpen.set(false)"
      >
        @for (o of filtered(); track o.value) {
          <mat-option [value]="o" [disabled]="o.disabled">{{ o.label }}</mat-option>
        }
      </mat-autocomplete>

      @if (panelOpen() && hasValue()) {
        <button
          type="button"
          hlmBtn
          variant="ghost"
          size="icon"
          tabindex="-1"
          class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
          (mousedown)="clear($event)"
          (click)="clear($event)"
          aria-label="Clear selection"
        >
          <hlm-icon name="lucideX" size="0.85rem" />
        </button>
      } @else {
        <hlm-icon
          name="lucideChevronsUpDown"
          size="0.9rem"
          class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
      }
    </div>
  `,
})
export class HlmComboboxComponent<T = unknown> implements ControlValueAccessor {
  readonly options = input<HlmSelectOption<T>[]>([]);
  readonly value = model<T | null>(null);
  readonly placeholder = input('Select…');
  /** Forwarded to the inner input so an external `<label for>` still associates.
   * Named `inputId` (not `id`) so the static attribute isn't also reflected onto
   * the host element — which would create a duplicate id. */
  readonly inputId = input<string | null>(null);

  readonly panelOpen = signal(false);
  private readonly query = signal('');
  readonly searchCtrl = new FormControl<string | HlmSelectOption<T>>('', { nonNullable: true });

  private onChange: (v: T | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor() {
    // Keep the displayed label in sync with `options()`, which can arrive AFTER the value was
    // written: a `formControlName` write runs synchronously (e.g. from an HTTP callback) while
    // the new `[options]` array only reaches this input on the next change detection. Resolving
    // the label here instead of snapshotting it in `writeValue` stops the field from showing the
    // previous list's entry — or nothing at all — for a value that is perfectly valid (EB-4209).
    effect(() => {
      const value = this.value();
      const options = this.options();
      // A null value means "nothing committed": either cleared, or the operator is typing a
      // query. Never touch `searchCtrl` then, or the query would be wiped mid-keystroke.
      if (value === null || value === undefined) return;
      const option = options.find((o) => o.value === value) ?? null;
      if (option !== null && this.searchCtrl.value !== option) {
        this.searchCtrl.setValue(option, { emitEvent: false });
      }
    });
  }

  readonly hasValue = computed(() => this.value() !== null && this.value() !== undefined);

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const opts = this.options();
    return q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts;
  });

  displayFn = (o: string | HlmSelectOption<T> | null): string =>
    !o ? '' : typeof o === 'string' ? o : o.label;

  onInput(): void {
    const v = this.searchCtrl.value;
    if (typeof v === 'string') {
      this.query.set(v);
      // Typing frees the committed value until an option is re-selected
      // (mirrors the Add-Billing category/feature behaviour).
      if (this.value() !== null) {
        this.value.set(null);
        this.onChange(null);
      }
    }
  }

  onSelected(event: MatAutocompleteSelectedEvent): void {
    const opt = event.option.value as HlmSelectOption<T>;
    this.value.set(opt.value);
    this.onChange(opt.value);
    this.searchCtrl.setValue(opt);
    this.query.set('');
  }

  clear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchCtrl.setValue('');
    this.value.set(null);
    this.onChange(null);
    this.query.set('');
    this.panelOpen.set(false);
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  /** Select all text on focus so the user can type a fresh query immediately. */
  selectAll(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    setTimeout(() => input.select(), 0);
  }

  writeValue(v: T | null): void {
    this.value.set(v ?? null);
    // Clearing is immediate; a non-null value gets its label from the effect above, which also
    // re-resolves it once a late `options()` array arrives.
    if (v == null) this.searchCtrl.setValue('', { emitEvent: false });
    this.query.set('');
  }
  registerOnChange(fn: (v: T | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void {
    if (d) this.searchCtrl.disable();
    else this.searchCtrl.enable();
  }
}
